import { MultipleButtonFloating, SearchInput, Typography } from "@/components";
import { BackAppBar } from "@/components/ui";
import useBackward from "@/hooks/useBackward";
import useBulletinMutation from "@/hooks/useBulletinMutation";
import { useDebounce } from "@/hooks/useDebounce";
import useProfile from "@/hooks/useProfile";
import usePromptCollection from "@/hooks/usePromptCollection";
import { cn } from "@/libraries/cn";
import { QUERY_KEYS } from "@/libraries/constant";
import {
	getBulletinGroupsOptions,
	getBulletinSubscriptionGroupsOptions,
} from "@/queries";
import type { BulletinGroup } from "@/types";
import Checkbox from "@mui/material/Checkbox";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { onlineManager, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import groupBy from "lodash/groupBy";
import { useMemo } from "react";
import { type SubmitHandler, useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";

const BulletinSettingSearchSchema = z.object({
	q: z.string().optional(),
});
type BulletinSettingSearchSchema = z.infer<typeof BulletinSettingSearchSchema>;

interface FormField extends BulletinGroup {
	isChecked: boolean;
}
interface FormFieldSchema {
	myGroups: FormField[];
	publicGroups: FormField[];
}

export const Route = createFileRoute("/dashboard/bulletin/setting")({
	validateSearch: zodSearchValidator(BulletinSettingSearchSchema),
	loader: ({ context }) => {
		if (onlineManager.isOnline() && !context.isRestoring) {
			return [
				context.queryClient.ensureQueryData(
					getBulletinSubscriptionGroupsOptions(),
				),
				context.queryClient.ensureQueryData(getBulletinGroupsOptions()),
			];
		}
		return [
			context.queryClient.getQueryData([
				QUERY_KEYS.GET_BULLETIN_SUBSCRIPTION_GROUPS,
			]),
			context.queryClient.getQueryData([QUERY_KEYS.GET_BULLETIN_GROUPS]),
		];
	},
	component: RouteComponent,
});

function RouteComponent() {
	const onBack = useBackward();
	const navigate = Route.useNavigate();
	const searchQ = Route.useSearch({
		select: (state: BulletinSettingSearchSchema) => state.q ?? "",
	});
	const debounceSearchQ = useDebounce(searchQ, 300);
	const subscribeGroupsQuery = useSuspenseQuery(
		getBulletinSubscriptionGroupsOptions(),
	);
	const groupsQuery = useSuspenseQuery(getBulletinGroupsOptions());
	const { profile } = useProfile();

	const formMethod = useForm<FormFieldSchema>({
		mode: "all",
		defaultValues: {
			myGroups: groupsQuery.data.my_groups.map((group) => ({
				...group,
				type: "my",
				isChecked: subscribeGroupsQuery.data.some(
					(subscribedGroup) => subscribedGroup.id === group.id,
				),
			})),
			publicGroups: groupsQuery.data.publics.map((group) => ({
				...group,
				type: "public",
				isChecked: subscribeGroupsQuery.data.some(
					(subscribedGroup) => subscribedGroup.id === group.id,
				),
			})),
		},
	});
	const {
		control,
		handleSubmit,
		formState: { isDirty, isSubmitting },
	} = formMethod;
	const formMyGroupsMethod = useFieldArray({
		control: control,
		name: "myGroups",
		keyName: "_id",
	});
	const formPublicGroupsMethod = useFieldArray({
		control: control,
		name: "publicGroups",
		keyName: "_id",
	});

	const groups = useMemo(() => {
		const items = formMyGroupsMethod.fields.concat(
			formPublicGroupsMethod.fields,
		);
		return groupBy(items, "type");
	}, [formMyGroupsMethod.fields, formPublicGroupsMethod.fields]);

	const groupEntries = Object.entries(groups);

	const onChangeChekcbox = (type: "my" | "public", fieldIndex: number) => {
		const targetFields =
			type === "my" ? formMyGroupsMethod : formPublicGroupsMethod;
		const selectedGroup = targetFields.fields[fieldIndex];
		if (!selectedGroup) return;
		targetFields.update(fieldIndex, {
			...selectedGroup,
			isChecked: !selectedGroup.isChecked,
		});
	};

	const { subscribe } = useBulletinMutation();
	const { upgradeBulletin } = usePromptCollection({
		currentPlan: profile?.subscription_status,
	});

	const onSubmit: SubmitHandler<FormFieldSchema> = async (data, e) => {
		e?.stopPropagation();
		e?.preventDefault();
		const mergedGroups = data.myGroups.concat(data.publicGroups);
		const newSubscribedGroups = mergedGroups.filter((group) => group.isChecked);
		if (
			profile?._isFreePlan &&
			profile?._maxSubsribeBulletin &&
			newSubscribedGroups.length > profile?._maxSubsribeBulletin
		) {
			return upgradeBulletin();
		}
		if (!newSubscribedGroups.length) return;
		await subscribe.mutateAsync(newSubscribedGroups.map((group) => group.id));
		return navigate({
			to: "/dashboard/bulletin",
		});
	};

	const title = useMemo(() => {
		if (profile?._isFreePlan && groups) {
			const selectedCount = [
				...(groups.my ?? []),
				...(groups.public ?? []),
			].filter((group) => group.isChecked);
			return `Subscribed Bulletins ${selectedCount.length}/${profile._maxSubsribeBulletin}`;
		}
		return "Subscribed Bulletins";
	}, [profile, groups]);

	return (
		<>
			<BackAppBar title={title}>
				<div className="container pb-2">
					<SearchInput
						name="search"
						label="Search"
						placeholder="Search"
						value={searchQ}
						onChange={(e) =>
							navigate({
								replace: true,
								search: {
									q: e.target.value || undefined,
								},
							})
						}
					/>
				</div>
			</BackAppBar>
			<form
				onSubmit={handleSubmit(onSubmit)}
				className="relative h-full w-full py-4 bg-white overflow-y-auto"
			>
				{groupEntries.map(([type, items]) => (
					<List
						key={type}
						subheader={
							<Typography fontWeight={600} fontSize={14} className="px-4">
								{type === "my" ? "My Groups" : "Public"}
							</Typography>
						}
					>
						{items.map((item, fieldIndex) => {
							const matchSearch = item.name
								.toLowerCase()
								.includes(debounceSearchQ.toLowerCase());
							if (!matchSearch) return null;
							return (
								<ListItem
									key={item._id}
									disablePadding
									className={cn({
										"!bg-primary-light": item.isChecked,
									})}
								>
									{/* biome-ignore lint/a11y/useValidAriaRole: <explanation> */}
									<ListItemButton
										role={undefined}
										dense
										onClick={() =>
											onChangeChekcbox(type as "my" | "public", fieldIndex)
										}
									>
										<ListItemIcon>
											<Checkbox
												edge="start"
												checked={item.isChecked}
												tabIndex={-1}
												disableRipple
												inputProps={{ "aria-labelledby": item.name }}
											/>
										</ListItemIcon>
										<ListItemText id={item.name} primary={item.name} />
									</ListItemButton>
								</ListItem>
							);
						})}
					</List>
				))}
				<MultipleButtonFloating
					positiveButtonProps={{
						type: "submit",
						label: "Save",
						disabled: !isDirty || isSubmitting,
					}}
					negativeButtonProps={{
						type: "button",
						label: "Cancel",
						onClick: () => onBack(),
					}}
				/>
			</form>
		</>
	);
}
